/* AstaLab 2.0 — /dashboard: "Le mie aste" (obiettivo 6) + import backup 1.3 (obiettivo 9). */
import { escapeHtml } from "../components/escapeHtml.js";
import { importLegacyBackup } from "../../legacy/importLegacyBackup.js";
import { logClientError } from "../../utils/clientLogger.js";

function fmtDate(iso){
  if(!iso) return "—";
  try{ return new Date(iso).toLocaleString("it-IT"); }catch(e){ return iso; }
}

// Round Beta-P3-7 (design D/D2, accettato): contatore di generazione module-private, incrementato
// come PRIMISSIMA istruzione di OGNI render completo di questa pagina — mai esposto pubblicamente.
// Serve SOLO al fallback secondario più sotto per sapere se il proprio tentativo di render è stato
// superato da uno più recente (stessa route o no) — un caso che il solo confronto di hash non può
// rilevare per un rientro sulla STESSA route (round P3-7D2 §1): un utente che lascia e rientra su
// #/dashboard produce un hash IDENTICO a quello del tentativo vecchio, ma un render completamente
// nuovo e già riuscito, che il fallback del tentativo vecchio non deve mai sovrascrivere.
let _dashboardRenderGeneration = 0;

// Round PB-H3 (pre-Beta hardening): hashAtEntry catturato QUI, prima di qualunque await — protegge
// SOLO contro la staleness da navigazione (l'utente lascia /dashboard mentre listAuctions() è ancora
// in volo). Un hash diverso da quello di ingresso significa che una destinazione più recente ha già
// preso il posto di questo container — questo render, per quanto riuscito o fallito, non deve mai
// scriverci sopra.
export async function renderDashboardPage(container, ctx){
  _dashboardRenderGeneration += 1;
  container.innerHTML = '<div class="dashboard"><p class="hint">Caricamento aste…</p></div>';
  const hashAtEntry = window.location.hash;
  // Round Beta-P3-4 (design D/D2, accettato): catch NARROW, dedicato SOLO a getCurrentUser() — mai
  // condiviso con listAuctions() sotto, la cui semantica di fallimento resta un problema distinto
  // (letture aste, non verifica sessione). Un rigetto qui significa "non è stato possibile
  // determinare lo stato di autenticazione" (vedi BackendAdapter.js/SupabaseBackendAdapter.js) — mai
  // trattato come "non autenticato": nessun redirect a /login, una card di retry veritiera invece,
  // testualmente distinta dalla card di errore di listAuctions() sotto. `null` resta l'UNICO segnale
  // autoritativo di sessione assente (comportamento invariato).
  let user;
  try{
    user = await ctx.adapter.getCurrentUser();
  }catch(err){
    if(window.location.hash !== hashAtEntry) return;
    container.innerHTML = renderDashboardAuthUncertainCard();
    wireDashboardAuthUncertainCard(container, ctx);
    return;
  }
  if(window.location.hash !== hashAtEntry) return;
  if(!user){ ctx.navigate("/login"); return; }

  // PB-H3: catch ristretto alla SOLA lettura listAuctions() — mai renderShell()/wire() sotto, mai
  // getCurrentUser() sopra. Un fallimento qui (rete/backend, mai un bug applicativo) produce una
  // card dedicata con retry, invece di un rigetto non gestito che lascia l'utente bloccato su
  // "Caricamento aste…" per sempre (comportamento precedente, invariato per ogni altra eccezione:
  // resta comunque un rigetto genuino, mai silenziosamente assorbito).
  let auctions;
  try{
    auctions = await ctx.repository.listAuctions();
  }catch(err){
    if(window.location.hash !== hashAtEntry) return;
    container.innerHTML = renderDashboardEntryErrorCard();
    wireDashboardEntryErrorCard(container, ctx);
    return;
  }
  if(window.location.hash !== hashAtEntry) return;
  container.innerHTML = renderShell(user, auctions);
  wire(container, ctx);
}

function renderDashboardEntryErrorCard(){
  return (
    '<div class="dashboard">' +
      '<div class="card" data-dashboard-entry-error>' +
        "<h3>Impossibile caricare le tue aste</h3>" +
        '<p class="errtext">Non è stato possibile caricare l\'elenco delle tue aste. Riprova più tardi.</p>' +
        '<button type="button" class="btn ghost sm" data-dashboard-entry-retry>Riprova</button>' +
      "</div>" +
    "</div>"
  );
}

// Round Beta-P3-4: card testualmente DISTINTA da renderDashboardEntryErrorCard() sopra — descrive
// un'incertezza sulla sessione, mai "sessione scaduta"/"disconnesso"/"account non valido" (nessuna
// di queste è provata dalla sola incertezza di lettura, vedi BackendAdapter.js).
function renderDashboardAuthUncertainCard(){
  return (
    '<div class="dashboard">' +
      '<div class="card" data-dashboard-auth-uncertain>' +
        "<h3>Impossibile verificare la sessione</h3>" +
        '<p class="errtext">Non è stato possibile verificare la tua sessione. Riprova.</p>' +
        '<button type="button" class="btn ghost sm" data-dashboard-auth-uncertain-retry>Riprova</button>' +
      "</div>" +
    "</div>"
  );
}

// Round Beta-P3-7 (design D/D2, accettato): UNICO punto da cui ogni retry di questa pagina richiama
// il render completo — mai duplicato per ciascun sito. `operation` preserva l'identità del log P3-5
// già accettata per il sito CHE HA AVVIATO il tentativo (entry-retry/auth-uncertain-retry), riusata
// invariata anche per ogni successivo tentativo dal fallback generico sotto (stessa lineage, mai
// un'etichetta nuova). La cattura di hash+generazione avviene SUBITO DOPO l'invocazione, mai prima
// (P3-7D2 §5-6: un valore catturato prima verrebbe invalidato dall'incremento di QUESTO STESSO
// tentativo, rendendo il confronto sempre "stale" per costruzione). Il log diagnostico avviene
// SEMPRE, anche per un tentativo ormai stale — la diagnosi di un fallimento reale non deve mai
// dipendere da chi altro possiede la pagina in quel momento (P3-7 §2); solo la scrittura del
// fallback è condizionata al possesso ancora attuale della route.
function attemptDashboardReload(container, ctx, operation){
  return async () => {
    const hashAtRetryStart = window.location.hash;
    const renderPromise = renderDashboardPage(container, ctx);
    const attemptedGeneration = _dashboardRenderGeneration;
    try{
      await renderPromise;
    }catch(e){
      // Difesa in profondità: renderDashboardPage() non lancia mai per un fallimento di
      // listAuctions()/getCurrentUser() (già try/catch-ati al suo interno) — questo ramo copre solo
      // un imprevisto realmente eccezionale, mai atteso in condizioni normali.
      logClientError({ area:"dashboard", operation, error:e });
      const stale = _dashboardRenderGeneration !== attemptedGeneration || window.location.hash !== hashAtRetryStart;
      if(stale) return;
      renderDashboardSecondaryFallback(container, ctx, operation);
    }
  };
}

// Round Beta-P3-7: card GENERICA per un fallimento inatteso del render completo di retry — mai una
// delle card di primo livello sopra (che restano invariate per il loro esito già gestito): questa
// non pretende di sapere quale dipendenza sia fallita, solo che il tentativo di ricaricare la
// pagina stesso non è riuscito. Il suo stesso pulsante richiama di nuovo lo stesso helper — riarmato
// indefinitamente, mai un pulsante disabilitato in modo permanente.
function renderDashboardSecondaryFallback(container, ctx, operation){
  container.innerHTML =
    '<div class="dashboard">' +
      '<div class="card" data-dashboard-secondary-fallback>' +
        "<h3>Impossibile ricaricare</h3>" +
        '<p class="errtext">Non è stato possibile ricaricare le tue aste. Riprova.</p>' +
        '<button type="button" class="btn ghost sm" data-dashboard-secondary-retry>Riprova</button>' +
      "</div>" +
    "</div>";
  const retryBtn = container.querySelector("[data-dashboard-secondary-retry]");
  retryBtn.addEventListener("click", async () => {
    retryBtn.disabled = true;
    await attemptDashboardReload(container, ctx, operation)();
  });
}

// PB-H3/Beta-P3-4: retry = richiama per intero renderDashboardPage() tramite l'helper condiviso
// sopra, stesso principio già stabilito da A7-P1-9 per il riepilogo di completamento — mai una
// nuova lettura parziale, mai un nuovo store di stato.
function wireDashboardEntryErrorCard(container, ctx){
  const retryBtn = container.querySelector("[data-dashboard-entry-retry]");
  if(!retryBtn) return;
  retryBtn.addEventListener("click", async () => {
    retryBtn.disabled = true;
    await attemptDashboardReload(container, ctx, "entry-retry")();
  });
}

function wireDashboardAuthUncertainCard(container, ctx){
  const retryBtn = container.querySelector("[data-dashboard-auth-uncertain-retry]");
  if(!retryBtn) return;
  retryBtn.addEventListener("click", async () => {
    retryBtn.disabled = true;
    await attemptDashboardReload(container, ctx, "auth-uncertain-retry")();
  });
}

function renderShell(user, auctions){
  return (
    '<div class="dashboard">' +
      '<div class="flex between">' +
        '<h1>Le mie aste</h1>' +
        '<div class="flex gap8">' +
          '<a href="#/catalog" class="btn ghost">Catalogo &amp; statistiche</a>' +
          '<button class="btn ghost" id="logoutBtn">Esci (' + escapeHtml(user.email) + ')</button>' +
        '</div>' +
      '</div>' +
      '<div id="logoutMsg"></div>' +
      '<div class="card">' +
        '<h3>Nuova asta</h3>' +
        '<form id="newAuctionForm" class="flex gap8 wrap">' +
          '<input type="text" id="newAuctionName" placeholder="Nome asta" required style="flex:1; min-width:200px;">' +
          '<input type="text" id="newAuctionSeason" placeholder="Stagione (opzionale, es. 2026_27)" style="flex:1; min-width:200px;">' +
          '<button type="submit" class="btn primary">Nuova asta</button>' +
        '</form>' +
      '</div>' +
      '<div class="card">' +
        '<h3>Partecipa a un\'asta</h3>' +
        '<p class="hint">Hai ricevuto un codice di invito dal proprietario di un\'asta? Inseriscilo qui.</p>' +
        '<form id="joinAuctionForm" class="flex gap8 wrap">' +
          '<input type="text" id="joinAuctionCode" placeholder="Codice di invito (es. K7XP-Q9WM-RT23)" required style="flex:1; min-width:200px;">' +
          '<button type="submit" class="btn">Partecipa</button>' +
        '</form>' +
        '<div id="joinAuctionMsg"></div>' +
      '</div>' +
      '<div class="card">' +
        '<h3>Importa backup AstaLab 1.3</h3>' +
        '<p class="hint">Carica un file JSON esportato da AstaLab 1.3.0-beta.6 ("Crea backup"): verrà creata una nuova asta, senza modificare le altre.</p>' +
        '<input type="file" id="legacyImportFile" accept="application/json,.json">' +
        '<div id="legacyImportMsg"></div>' +
      '</div>' +
      '<div id="dashboardMsg"></div>' +
      renderAuctionsTable(auctions) +
    '</div>'
  );
}

function renderAuctionsTable(auctions){
  if(!auctions.length) return '<p class="hint">Nessuna asta ancora. Creane una qui sopra.</p>';
  return (
    '<div class="table-wrap"><table><thead><tr>' +
      "<th>Nome</th><th>Stagione</th><th>Stato</th><th>Ruolo</th><th>Aggiornata</th><th></th>" +
    "</tr></thead><tbody>" +
    auctions.map(a =>
      "<tr>" +
        "<td>" + escapeHtml(a.name) + "</td>" +
        "<td>" + escapeHtml(a.season || "—") + "</td>" +
        '<td><span class="badge ' + (a.status==="active"?"ok":"neutral") + '">' + escapeHtml(a.status) + "</span></td>" +
        "<td>" + escapeHtml(a.myRole) + "</td>" +
        "<td>" + escapeHtml(fmtDate(a.updatedAt)) + "</td>" +
        '<td><div class="flex gap8 wrap">' +
          '<button class="btn ghost sm" data-open="' + escapeHtml(a.id) + '">Apri</button>' +
          (a.myRole==="owner" ? '<button class="btn ghost sm" data-rename="' + escapeHtml(a.id) + '">Rinomina</button>' : "") +
          (a.myRole==="owner" && a.status==="active" ? '<button class="btn ghost sm" data-archive="' + escapeHtml(a.id) + '">Archivia</button>' : "") +
        "</div></td>" +
      "</tr>"
    ).join("") +
    "</tbody></table></div>"
  );
}

// Round Beta-P3-9 (design D, accettato): unica copia fissa per un logout non confermato riuscito —
// mai il valore {error} grezzo/mappato dell'adapter (l'unico fatto autorevole è "l'operazione non è
// stata confermata riuscita", mai "sessione scaduta"/"logout riuscito"/"accesso richiesto", nessuno
// dei quali è provato in questo ramo). Condivisa fra il ramo {ok:false} e quello di rigetto sotto —
// stesso identico messaggio, mai due varianti.
const LOGOUT_FAILURE_COPY = '<p class="errtext">Non è stato possibile completare la disconnessione. Riprova.</p>';

function wire(container, ctx){
  container.querySelector("#logoutBtn").addEventListener("click", async () => {
    // Round Beta-P3-9 (design D, accettato): riusa _dashboardRenderGeneration (mai un secondo
    // contatore, P3-7D2) — il logout stesso non avvia mai un render completo di questa pagina,
    // quindi qui non c'è (né serve) una cattura POST-chiamata come nell'helper di retry P3-7: la
    // generazione che ci interessa è semplicemente quella che possiede già il pulsante cliccato,
    // catturata all'inizio del click, prima di qualunque await.
    const hashAtClickStart = window.location.hash;
    const attemptedGeneration = _dashboardRenderGeneration;
    const logoutMsg = container.querySelector("#logoutMsg");
    logoutMsg.innerHTML = "";
    let result;
    try{
      result = await ctx.adapter.signOut();
    }catch(e){
      // Round Beta-P3-9D §8: un rigetto non deve mai sfuggire come promise rejection non gestita —
      // mai una navigazione a /login solo perché la chiamata è fallita in questo modo.
      if(_dashboardRenderGeneration !== attemptedGeneration || window.location.hash !== hashAtClickStart) return;
      logoutMsg.innerHTML = LOGOUT_FAILURE_COPY;
      return;
    }
    // Round Beta-P3-9D §4/§7: la staleness va verificata PRIMA di decidere il ramo ok:true/ok:false —
    // copre sia un SIGNED_OUT già scaturito altrove (compreso il caso installato in cui l'SDK reale
    // rimuove comunque la sessione locale ed emette SIGNED_OUT pur restituendo {ok:false}) sia un
    // rientro su #/dashboard con un render completamente nuovo che ormai possiede il pulsante.
    if(_dashboardRenderGeneration !== attemptedGeneration || window.location.hash !== hashAtClickStart) return;
    if(result.ok){
      ctx.navigate("/login");
      return;
    }
    // Round Beta-P3-9D §6-7 (design D, accettato): {ok:false} con la Dashboard ancora proprietaria
    // della route/render significa che il logout NON è stato confermato riuscito — mai una
    // navigazione a /login su questo solo segnale (la sessione locale, per quanto risulta da questo
    // stesso adapter, può restare autenticata), mai un rollback/render completo solo per mostrarlo.
    logoutMsg.innerHTML = LOGOUT_FAILURE_COPY;
  });

  container.querySelector("#newAuctionForm").addEventListener("submit", async (e) => {
    e.preventDefault();
    const name = container.querySelector("#newAuctionName").value;
    const season = container.querySelector("#newAuctionSeason").value || null;
    const msg = container.querySelector("#dashboardMsg");
    const result = await ctx.repository.createAuction({ name, season });
    if(!result.ok){ msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error) + "</p>"; return; }
    renderDashboardPage(container, ctx);
  });

  container.querySelector("#joinAuctionForm").addEventListener("submit", async (e) => {
    e.preventDefault();
    const codeInput = container.querySelector("#joinAuctionCode");
    const msg = container.querySelector("#joinAuctionMsg");
    const submitBtn = e.target.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    const result = await ctx.repository.joinAuctionByCode(codeInput.value);
    submitBtn.disabled = false;
    if(!result.ok){ msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Impossibile partecipare con questo codice.") + "</p>"; return; }
    msg.innerHTML = '<p class="okmsg">' + (result.alreadyMember ? "Fai già parte di questa asta." : "Ti sei unito all'asta.") + "</p>";
    renderDashboardPage(container, ctx);
  });

  container.querySelectorAll("[data-open]").forEach(btn=>{
    btn.addEventListener("click", () => ctx.navigate("/auction/" + btn.getAttribute("data-open")));
  });
  container.querySelectorAll("[data-rename]").forEach(btn=>{
    btn.addEventListener("click", async () => {
      const id = btn.getAttribute("data-rename");
      const name = (typeof prompt === "function") ? prompt("Nuovo nome dell'asta:", "") : null;
      if(!name) return;
      const result = await ctx.repository.renameAuction(id, name);
      const msg = container.querySelector("#dashboardMsg");
      if(!result.ok){ msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error) + "</p>"; return; }
      renderDashboardPage(container, ctx);
    });
  });
  // Round Beta-P3-1 (design Beta-P3-1D §3/§8): archiveAuction() non ha un ripristino nel prodotto —
  // window.confirm() PRIMA di qualunque mutazione, mai dopo aver disabilitato il pulsante (un
  // annullamento non deve mai alterare lo stato del controllo). Questo round chiude anche la lacuna
  // di protezione da doppia attivazione mai risolta finora su questa azione: btn.disabled=true SOLO
  // dopo la conferma, PRIMA dell'unico await.
  container.querySelectorAll("[data-archive]").forEach(btn=>{
    btn.addEventListener("click", async () => {
      if(!window.confirm("Vuoi archiviare questa asta? Resterà accessibile, ma non accetterà nuovi ingressi e non è disponibile un ripristino.")) return;
      const id = btn.getAttribute("data-archive");
      btn.disabled = true;
      const result = await ctx.repository.archiveAuction(id);
      const msg = container.querySelector("#dashboardMsg");
      if(!result.ok){
        btn.disabled = false;
        msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error) + "</p>";
        return;
      }
      renderDashboardPage(container, ctx);
    });
  });

  container.querySelector("#legacyImportFile").addEventListener("change", async (e) => {
    const file = e.target.files && e.target.files[0];
    const msg = container.querySelector("#legacyImportMsg");
    if(!file) return;
    msg.innerHTML = '<p class="hint">Importazione in corso…</p>';
    try{
      const text = await file.text();
      let parsed;
      try{ parsed = JSON.parse(text); }
      catch(err){ msg.innerHTML = '<p class="errtext">File JSON non valido.</p>'; return; }
      const result = await importLegacyBackup(ctx.repository, parsed, {});
      if(!result.ok){ msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error) + "</p>"; return; }
      msg.innerHTML = '<p class="okmsg">Backup importato in una nuova asta: "' + escapeHtml(result.auction.name) + '".</p>';
      renderDashboardPage(container, ctx);
    }catch(err){
      msg.innerHTML = '<p class="errtext">Import fallito: ' + escapeHtml(err && err.message ? err.message : String(err)) + "</p>";
    }
  });
}
